import React, { useEffect, useRef, useState } from 'react'
import "./chatGPT.scss"
import { CopyOutlined, SendOutlined } from '@ant-design/icons';
import { message, Image, Input, Tooltip } from 'antd';
// import { setMinutes } from 'date-fns';
import SimpleBarReact from "simplebar-react";
import axios from 'axios';
import Utils from '../../misc/Utils';
import Enums from '../../misc/Enums';
import { normalizeChatResponse } from './chatResponseUtils';

const DAILY_CHAT_CACHE_VERSION = 1;
const AI_GREETING =
   'Greetings! I am Brainee Bot, your Brainee LMS assistant. Ask me for a step-by-step guide to any LMS module, such as Lessons, Assessments, Attendance, or Reports.';

const getLocalDateKey = () => {
   const now = new Date();
   const year = now.getFullYear();
   const month = String(now.getMonth() + 1).padStart(2, '0');
   const day = String(now.getDate()).padStart(2, '0');
   return `${year}-${month}-${day}`;
};

const getDailyChatCacheKey = (userId) => `brainee-dashboard-chat:${userId || 'unknown'}`;

const loadDailyChat = (cacheKey) => {
   try {
      const cachedValue = localStorage.getItem(cacheKey);
      if (!cachedValue) return null;

      const cachedChat = JSON.parse(cachedValue);
      const isCurrentCache = cachedChat?.version === DAILY_CHAT_CACHE_VERSION
         && cachedChat?.date === getLocalDateKey()
         && Array.isArray(cachedChat?.storedValues)
         && Array.isArray(cachedChat?.chatMessages);

      if (isCurrentCache) return cachedChat;

      localStorage.removeItem(cacheKey);
   } catch {
      // Chat still works when browser storage is unavailable or contains invalid data.
   }

   return null;
};

export function ChatGPT(props) {
   const userRole = Utils.getCurrentUserInfo(Enums.UserInfo.role);
   const userId = Utils.getCurrentUserInfo(Enums.UserInfo.id);
   const cacheKey = getDailyChatCacheKey(userId);
   const initialChat = useRef();
   if (initialChat.current === undefined)
      initialChat.current = props.persistDaily ? loadDailyChat(cacheKey) : null;
   const [storedValues, setStoredValues] = useState(() => initialChat.current?.storedValues || [{
      type: "answer",
      message: AI_GREETING,
   }]);
   const [newQuestion, setNewQuestion] = useState('');
   const [chatMessages, setMessages] = useState(() => initialChat.current?.chatMessages || []);
   const [isTyping, setIsTyping] = useState(false);
   const [botSearching, setBotSearching] = useState(false);
   const { TextArea } = Input;
   const messagesEndRef = useRef(null);
   const messageInputRef = useRef(null);

   useEffect(() => {
      // Focus input on mount.
      messageInputRef.current?.focus();
   }, []);

   const focusMessageInput = () => {
      // Wait for state updates so the disabled TextArea becomes focusable again.
      requestAnimationFrame(() => {
         messageInputRef.current?.focus();
      });
   };

   const scrollToBottom = (behavior = "smooth") => {
      requestAnimationFrame(() => {
         requestAnimationFrame(() => {
            messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
         });
      });
   };

   const copyText = (text) => {
      navigator.clipboard.writeText(text);
      const key = 'updatable';
      message.info({ content: 'Copied to clipboard!', key });
   };

   const generateResponse = async () => {
      const question = newQuestion.trim();

      if (question !== '') {
         setBotSearching(true);

         var tmpMessage = [...chatMessages];
         tmpMessage.push({ "role": "user", "content": question });
         setMessages(tmpMessage);

         try {
            var tmpStoreMessage = [...storedValues];
            tmpStoreMessage.push({
               type: "question",
               message: question
            })
            setStoredValues(tmpStoreMessage);
            setNewQuestion('');
            // Scroll down to the bottom
            setIsTyping(true);
            scrollToBottom("smooth");

            var url = `${process.env.REACT_APP_API_CPH}/openai/brainee-chat`;
            const response = await axios.post(url, {
               userRole,
               messages: tmpMessage
            });

            setIsTyping(false);

            if (response.data.choices) {
               const botResponse = normalizeChatResponse(response.data.choices[0].message?.content);

               const completedStoreMessages = [...tmpStoreMessage, {
                  type: "answer",
                  message: botResponse
               }];
               const completedChatMessages = [...tmpMessage, {
                  role: "assistant",
                  content: botResponse,
               }];

               setStoredValues(completedStoreMessages);
               setMessages(completedChatMessages);
               scrollToBottom("smooth");               
            }            
         }
         catch (ex) {
            setIsTyping(false);
            message.warning("Error connecting to the server. Please try again.");
            //console.log(ex);
         }

         setBotSearching(false);
         focusMessageInput();
      } else
         setNewQuestion('');
   };

   useEffect(() => {
      if (!props.persistDaily) return;

      try {
         localStorage.setItem(cacheKey, JSON.stringify({
            version: DAILY_CHAT_CACHE_VERSION,
            date: getLocalDateKey(),
            storedValues,
            chatMessages,
         }));
      } catch {
         // Ignore full or unavailable browser storage and keep the in-memory chat usable.
      }
   }, [cacheKey, chatMessages, props.persistDaily, storedValues]);

   useEffect(() => {
      scrollToBottom("smooth");
   }, [storedValues.length, isTyping, botSearching]);

   const handleInputPressEnter = (event) => {
      if (event.shiftKey) return;

      event.preventDefault();
      if (!botSearching) generateResponse();
   };   

   return (
      <>
         <div className="form-section">
            <div className="answer-container" >
               <SimpleBarReact style={{ maxHeight: "calc(100vh - 361px)" }}>
                  <div style={{ display: "flex", justifyContent: "center", paddingTop: "10px", paddingBottom: "20px" }}>
                  </div>

                  {storedValues.map((value, index) => {
                     return (
                        <div className="answer-section" key={index}>
                           {value.type === "answer" &&
                              <>
                                 <div style={{ display: "flex", justifyContent: "left" }}>
                                    <div style={{ paddingLeft: "5px", marginTop: "10px" }}>
                                       <Image src='../images/brainee_bot.png' preview={false} height="40px" />
                                       
                                    </div>
                                    <div style={{ maxWidth: "65%" }}>
                                       <pre className="answer">{value.message}</pre>
                                    </div>
                                 </div>
                                 <div className="copy-icon">
                                    <Tooltip placement="top" title='Click to copy text'>
                                       <CopyOutlined onClick={() => copyText(value.message)} />
                                    </Tooltip>
                                 </div>
                              </>
                           }
                           {value.type === "question" &&
                              <div style={{ display: "flex", justifyContent: "right" }}>
                                 <div style={{ maxWidth: "65%" }}>
                                    <p className="question">{value.message}</p>
                                 </div>
                              </div>}
                        </div>
                     );
                  })}
                  {isTyping &&
                     <div style={{ display: "flex" }}>
                        <div style={{ paddingLeft: "5px", marginLeft: "10px", marginTop: "10px", marginBottom: '10px' }}>
                           <Image src='../images/brainee_bot.png' preview={false} height="40px" />
                        </div>
                        <div className="typing">
                           <div className="typing__dot" />
                           <div className="typing__dot" />
                           <div className="typing__dot" />
                        </div>
                     </div>}
                  <div ref={messagesEndRef} />
               </SimpleBarReact>
            </div>

            <div className="chatgpt-composer">
               <TextArea
                  ref={messageInputRef}
                  value={newQuestion}
                  maxLength={1000}
                  placeholder="Ask Brainee Bot..."
                  onChange={(e) => setNewQuestion(e.target.value)}
                  onPressEnter={handleInputPressEnter}
                  autoSize={{
                     minRows: 1,
                     maxRows: 5,
                  }}
                  disabled={botSearching}
                  className="chatgpt-composer-input"
               />
               <button
                  type="button"
                  className="chatgpt-composer-send"
                  disabled={botSearching || newQuestion.trim() === ""}
                  onClick={() => generateResponse()}
                  aria-label="Send message"
               >
                  <SendOutlined />
               </button>
               <div className="chatgpt-composer-hint">{botSearching ? "Brainee Bot is responding..." : "Press Enter to send, Shift+Enter for new line"}</div>
            </div>
         </div>
      </>
   )
}
